import { useCallback, useEffect, useState } from 'react';

import { Slide } from '../components/SlideComponents/slide.types';
import { ById } from '../services/backendService/types';
import { Template } from '../services/TemplateService/Template.type';
import { getTitle } from '../utils/templateUtils';
import { useBreakPoint } from './useBreakPoint';
import useCreateGameFromTemplate from './useCreateGameFromTemplate';
import useFullScreen from './useFullScreen';
import useGameAutoSave from './useGameAutoSaver';
import useRouterMatch from './useRouterMatch';

const usePresent = () => {
  const isDesktop = useBreakPoint(900);
  const [isCreatingGameCurimotePressed, setIsCreatingGameCurimotePressed] = useState(
    false,
  );

  const { autoSave: autoSaveGame } = useGameAutoSave();
  const { goFullScreen } = useFullScreen();
  const [isCreatingGamePresentPressed, setIsCreatingGamePresentPressed] = useState(false);
  const {
    createGameFromTemplate,
    isCreatingGameFromTemplate,
  } = useCreateGameFromTemplate();

  useEffect(() => {
    return () => {
      setIsCreatingGameCurimotePressed(false);
      setIsCreatingGamePresentPressed(false);
    };
  }, []);
  const { tenantId, router } = useRouterMatch();
  const openCurimote = useCallback(
    (gameId?: string | null) => {
      if (gameId) {
        const link = window.open(
          `${window.location.origin}/${tenantId}/games/${gameId}/curimote`,
          'curimote',
          `width=${window.innerWidth * 0.6},height=${window.innerHeight * 0.6}`,
        );
        if (link) {
          link.opener = null;
        }
      }
    },
    [tenantId],
  );
  const routeToLiveAndFullScreen = useCallback(
    (gameId: string) => {
      router.push(`/${tenantId}/games/${gameId}/live`);
      goFullScreen();
    },
    [router, tenantId, goFullScreen],
  );

  const presentLesson = useCallback(
    async ({
      template,
      fromBeginning,
      method,
      gameId,
    }: {
      template: Template;
      gameId?: string | null;
      method: 'present' | 'curimote';
      fromBeginning?: boolean;
    }) => {
      // private and workspace present
      if (template.content.slides && template.content.slides.length > 0) {
        if (method === 'curimote') setIsCreatingGameCurimotePressed(true);
        if (method === 'present') setIsCreatingGamePresentPressed(true);
        if (gameId) {
          // saves the first round as currentRound before routing;
          if (fromBeginning) {
            await autoSaveGame({
              gameId,
              currentRound: template.content.slides[0].id,
              modifiedAt: Date.now(),
            });
            if (method === 'curimote' && isDesktop) {
              openCurimote(gameId);
            }
            return routeToLiveAndFullScreen(gameId);
          }

          // Routes the user to live;
          if (method === 'curimote' && isDesktop) {
            openCurimote(template.content.activeGame?.id);
          }
          return routeToLiveAndFullScreen(gameId);
        }

        // There is no game, and we need to create it before routing
        if (
          !template.content.activeGame ||
          (template.content.activeGame && !template.content.activeGame.id)
        ) {
          const title = getTitle({
            title: template.title,
            slides: template.content.slides,
          });
          const roundToStartFrom = template.currentRound
            ? template.currentRound
            : template.content.slides?.[0].id;
          createGameFromTemplate({
            activeTenantId: tenantId,
            templateConfig: {
              title: title,
              id: template.id,
              slides: template.content.slides,
              metadata: template.metadata,
              modifiedAt: new Date().getTime(),
              requireRealNames: template.requireRealNames,
              currentRound: roundToStartFrom,
            },
            onSuccessFunction: (gameId: string) => {
              if (method === 'curimote' && isDesktop) {
                openCurimote(gameId);
              }
              return routeToLiveAndFullScreen(gameId);
            },
          });
        }
      }
    },
    [
      autoSaveGame,
      createGameFromTemplate,
      isDesktop,
      openCurimote,
      routeToLiveAndFullScreen,
      tenantId,
    ],
  );

  // lessons that is not owned by the users
  const presentPublicLesson = useCallback(
    ({
      template,
      isFromDiscover,
      method,
    }: {
      method: 'present' | 'curimote';
      template: {
        slides: Slide[];
        activeGame?: { id: string; isOngoing: boolean };
        title?: string;
        id: string;
        metadata?: ById<string | number | boolean> | null;
        requireRealNames?: boolean;
      };
      isFromDiscover?: boolean;
    }) => {
      // private and workspace present
      if (template.slides && template.slides.length > 0) {
        if (method === 'curimote') setIsCreatingGameCurimotePressed(true);
        if (method === 'present') setIsCreatingGamePresentPressed(true);
        if (template.activeGame?.id && !isFromDiscover) {
          return routeToLiveAndFullScreen(template.activeGame.id);
        }
        // There is no game, and we need to create it before routing
        const title = getTitle({ title: template.title, slides: template.slides });
        const roundToStartFrom = template.slides?.[0].id;
        createGameFromTemplate({
          isFromDiscover,
          activeTenantId: tenantId,
          templateConfig: {
            title: title,
            id: template.id,
            modifiedAt: new Date().getTime(),
            currentRound: roundToStartFrom,
            slides: template.slides,
            requireRealNames: template.requireRealNames,
          },
          onSuccessFunction: (gameId: string) => {
            return routeToLiveAndFullScreen(gameId);
          },
        });
      }
    },
    [createGameFromTemplate, routeToLiveAndFullScreen, tenantId],
  );

  return {
    presentLesson,
    presentPublicLesson,
    isCreatingGame:
      isCreatingGameFromTemplate ||
      isCreatingGameCurimotePressed ||
      isCreatingGamePresentPressed,
    isCreatingGameCurimote: isCreatingGameCurimotePressed,
    isCreatingGamePresent: isCreatingGamePresentPressed,
  };
};

export default usePresent;
